/**
 * Evaluation Scenarios API
 * Evaluation scenario management and execution endpoints
 * Matches the original Alpine.js implementation
 */

import apiClient from './client';
import type {
  EvalScenario,
  CreateEvalScenarioRequest,
  UpdateEvalScenarioRequest,
  PromoteChatRequest,
  EvalRunResult,
  AgentTypeOption,
  JudgerModelOption,
  EvalResultListResponse,
  EvalRunListResponse,
  EvalTestSuite,
  CreateTestSuiteRequest,
  UpdateTestSuiteRequest,
  TestSuiteListResponse,
  TestSuiteRunResult,
} from '@/types/evalScenarios';

interface EvalScenarioListResponse {
  scenarios: EvalScenario[];
}

interface CategoriesResponse {
  categories: string[];
}

interface AgentListResponse {
  agents: Array<{
    agent_type: string;
    name?: string;
  }>;
}

interface ModelsResponse {
  chat_models: Array<{
    id: string;
  }>;
}

export const evalScenariosApi = {
  /**
   * List evaluation scenarios with optional filters
   */
  list: async (params?: {
    category?: string;
    source?: string;
    is_active?: boolean;
  }): Promise<EvalScenario[]> => {
    const response = await apiClient.get<EvalScenarioListResponse>('/eval-scenarios', { params });
    return response.data.scenarios || [];
  },

  /**
   * Get a single scenario by ID
   */
  get: async (scenarioId: string): Promise<EvalScenario> => {
    const response = await apiClient.get<EvalScenario>(`/eval-scenarios/${scenarioId}`);
    return response.data;
  },

  /**
   * Create a new scenario
   */
  create: async (data: CreateEvalScenarioRequest): Promise<EvalScenario> => {
    const response = await apiClient.post<EvalScenario>('/eval-scenarios', data);
    return response.data;
  },

  /**
   * Update a scenario
   */
  update: async (scenarioId: string, data: UpdateEvalScenarioRequest): Promise<EvalScenario> => {
    const response = await apiClient.put<EvalScenario>(`/eval-scenarios/${scenarioId}`, data);
    return response.data;
  },

  /**
   * Delete a scenario
   */
  delete: async (scenarioId: string): Promise<void> => {
    await apiClient.delete(`/eval-scenarios/${scenarioId}`);
  },

  /**
   * Duplicate a scenario
   */
  duplicate: async (scenarioId: string): Promise<EvalScenario> => {
    const response = await apiClient.post<EvalScenario>(`/eval-scenarios/${scenarioId}/duplicate`);
    return response.data;
  },

  /**
   * Run evaluation for a single scenario
   * Uses longer timeout for LLM operations (5 minutes)
   */
  runSingle: async (scenarioId: string, userId?: string | null, responseLanguage?: string | null): Promise<EvalRunResult> => {
    console.log('[API] runSingle: POST /eval-scenarios/' + scenarioId + '/run', { userId, responseLanguage });
    const params: Record<string, string> = {};
    if (userId) params.user_id = userId;
    if (responseLanguage) params.response_language = responseLanguage;
    const response = await apiClient.post<EvalRunResult>(
      `/eval-scenarios/${scenarioId}/run`,
      {},
      { timeout: 300000, params } // 5 minutes for LLM evaluation
    );
    console.log('[API] runSingle response:', response.data);
    return response.data;
  },

  /**
   * Run batch evaluation for all active scenarios
   * Uses longer timeout for LLM operations (10 minutes for batch)
   */
  runBatch: async (userId?: string | null, responseLanguage?: string | null): Promise<EvalRunResult> => {
    console.log('[API] runBatch: POST /eval-scenarios/run', { userId, responseLanguage });
    const body: Record<string, string> = {};
    if (userId) body.user_id = userId;
    if (responseLanguage) body.response_language = responseLanguage;
    const response = await apiClient.post<EvalRunResult>(
      '/eval-scenarios/run',
      body,
      { timeout: 600000 } // 10 minutes for batch LLM evaluation
    );
    console.log('[API] runBatch response:', response.data);
    return response.data;
  },

  /**
   * Promote a chat conversation to a scenario
   */
  promoteChat: async (data: PromoteChatRequest): Promise<EvalScenario> => {
    const response = await apiClient.post<EvalScenario>('/eval-scenarios/promote-chat', data);
    return response.data;
  },

  /**
   * Get available categories
   */
  getCategories: async (): Promise<string[]> => {
    const response = await apiClient.get<CategoriesResponse>('/eval-scenarios/meta/categories');
    return response.data.categories || [];
  },

  /**
   * Get all unique tags across scenarios
   */
  getTags: async (): Promise<string[]> => {
    const response = await apiClient.get<{ tags: string[] }>('/eval-scenarios/meta/tags');
    return response.data.tags || [];
  },

  /**
   * Load agent types from agent configs API
   */
  getAgentTypes: async (): Promise<AgentTypeOption[]> => {
    try {
      const response = await apiClient.get<AgentListResponse>('/agent-configs/agents/list', {
        params: { include_inactive: false },
      });
      const agents = response.data.agents || response.data || [];
      return agents.map((a) => ({
        value: a.agent_type,
        label: a.name || a.agent_type,
      }));
    } catch (e) {
      console.error('[EvalScenarios] Load agent types error:', e);
      return [{ value: 'dora', label: 'Dora' }];
    }
  },

  /**
   * Load LLM models for judger selection
   */
  getJudgerModels: async (): Promise<JudgerModelOption[]> => {
    try {
      const response = await apiClient.get<ModelsResponse>('/models/all');
      const models = response.data.chat_models || [];
      return models.map((m) => ({
        value: m.id,
        label: m.id,
      }));
    } catch (e) {
      console.error('[EvalScenarios] Load judger models error:', e);
      return [
        { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
        { value: 'gpt-4o', label: 'gpt-4o' },
      ];
    }
  },

  // ============================================
  // Evaluation History APIs (Phase 1)
  // ============================================

  /**
   * Get historical evaluation results for a scenario
   */
  getScenarioResults: async (
    scenarioId: string,
    limit: number = 20
  ): Promise<EvalResultListResponse> => {
    const response = await apiClient.get<EvalResultListResponse>(
      `/eval-scenarios/${scenarioId}/results`,
      { params: { limit } }
    );
    return response.data;
  },

  /**
   * Get all evaluation runs list
   */
  listRuns: async (
    limit: number = 20,
    offset: number = 0
  ): Promise<EvalRunListResponse> => {
    console.log('[API] listRuns: GET /eval-scenarios/runs/list', { limit, offset });
    const response = await apiClient.get<EvalRunListResponse>(
      '/eval-scenarios/runs/list',
      { params: { limit, offset } }
    );
    console.log('[API] listRuns response:', response.data);
    return response.data;
  },

  /**
   * Get all results for a specific run
   */
  getRunResults: async (runId: string): Promise<EvalResultListResponse> => {
    const response = await apiClient.get<EvalResultListResponse>(
      `/eval-scenarios/runs/${runId}/results`
    );
    return response.data;
  },

  // ============================================
  // Export / Import
  // ============================================

  /**
   * Export all active scenarios as a portable JSON bundle
   */
  exportScenarios: async (params?: { category?: string; source_env?: string }): Promise<unknown> => {
    const response = await apiClient.get('/eval-scenarios/export', { params });
    return response.data;
  },

  /**
   * Import scenarios from an exported JSON bundle
   */
  importScenarios: async (bundle: unknown): Promise<{
    message: string;
    created: number;
    skipped: number;
    skipped_names: string[];
    total: number;
  }> => {
    const response = await apiClient.post('/eval-scenarios/import', bundle);
    return response.data;
  },
};

// ============================================
// Test Suite API
// ============================================

export const testSuitesApi = {
  /**
   * List all test suites with optional filtering
   */
  list: async (params?: { is_active?: boolean }): Promise<EvalTestSuite[]> => {
    const response = await apiClient.get<TestSuiteListResponse>('/test-suites', { params });
    return response.data.suites || [];
  },

  /**
   * Get a single test suite by ID
   */
  get: async (suiteId: string): Promise<EvalTestSuite> => {
    const response = await apiClient.get<EvalTestSuite>(`/test-suites/${suiteId}`);
    return response.data;
  },

  /**
   * Create a new test suite
   */
  create: async (data: CreateTestSuiteRequest): Promise<EvalTestSuite> => {
    const response = await apiClient.post<EvalTestSuite>('/test-suites', data);
    return response.data;
  },

  /**
   * Update a test suite
   */
  update: async (suiteId: string, data: UpdateTestSuiteRequest): Promise<EvalTestSuite> => {
    const response = await apiClient.put<EvalTestSuite>(`/test-suites/${suiteId}`, data);
    return response.data;
  },

  /**
   * Delete a test suite
   */
  delete: async (suiteId: string, hardDelete: boolean = false): Promise<void> => {
    await apiClient.delete(`/test-suites/${suiteId}`, { params: { hard_delete: hardDelete } });
  },

  /**
   * Run a test suite
   * Uses longer timeout for LLM operations (10 minutes for suite)
   */
  run: async (suiteId: string, modelId?: string, userId?: string | null, responseLanguage?: string | null): Promise<TestSuiteRunResult> => {
    console.log('[API] runSuite: POST /test-suites/' + suiteId + '/run', { userId, responseLanguage });
    const body: Record<string, string> = {};
    if (modelId) body.model_id = modelId;
    if (userId) body.user_id = userId;
    if (responseLanguage) body.response_language = responseLanguage;
    const response = await apiClient.post<TestSuiteRunResult>(
      `/test-suites/${suiteId}/run`,
      body,
      { timeout: 600000 } // 10 minutes for suite evaluation
    );
    console.log('[API] runSuite response:', response.data);
    return response.data;
  },

  /**
   * Get scenarios in a test suite
   */
  getScenarios: async (suiteId: string): Promise<EvalScenario[]> => {
    const response = await apiClient.get<EvalScenario[]>(`/test-suites/${suiteId}/scenarios`);
    return response.data;
  },

  /**
   * Get all unique tags from test suites
   */
  getTags: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/test-suites/meta/tags');
    return response.data;
  },
};
